import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-balham.css"; // Optional Theme applied to the grid
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";

import { globalAGGridTableHeight } from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";

// Set trade data from the store

import { star } from "@/components/icons/SVGIcons";
import {
  ColDef,
  ColumnState,
  GetDetailRowDataParams,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IDetailCellRendererParams,
} from "ag-grid-community";
import { useTheme } from "next-themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cubeCustomColors } from "../../../../../../tailwind.config";
import {
  interpolateColor,
  interpolateColorRankSpecific,
} from "../brokerRankHelperFunctions";
import { IBaseFilterBrokerRank } from "../BrokerRankIndex";

function GenerateBrokerRankStyles(theme: string | undefined) {
  if (theme == "light") {
    return {
      "--ag-background-color": cubeCustomColors.cubeTableRowColour[750],
      "--ag-odd-row-background-color": cubeCustomColors.cubeTableRowColour[750],
      "--ag-row-hover-color": cubeCustomColors.cubeTableRowColour[600], // Hover color
    };
  } else {
    return {
      "--ag-background-color": "transparent",
      "--ag-odd-row-background-color": "transparent",
      "--ag-row-hover-color": cubeCustomColors.cubeTableRowColour[800], // Hover color
    };
  }
}

function buildBrokerRankData(
  dataFlatterned: IFlattenedData[],
  allCPs: string[],
) {
  const allowedKeys = [...allCPs] as const;
  type AllowedKeys = (typeof allowedKeys)[number];
  type CpKeyType = {
    // eslint-disable-next-line no-unused-vars
    [K in AllowedKeys]?: number;
  };
  type RankItem = CpKeyType & {
    id: string;
    InstrumentType: string;
    children: any[];
  };
  type TFinalRank = RankItem[];
  const finalRank: TFinalRank = [];

  dataFlatterned.forEach((item) => {
    const baseItem = {
      id: item.id,
      InstrumentType: item.InstrumentType,
    } as RankItem;

    const currentValues = allCPs
      .map((cp) => item[cp] as number | undefined)
      .filter((c): c is number => c != undefined);

    if (currentValues.length === 0) return;

    item.ranks.forEach((rank) => {
      for (const key in rank) {
        if (Object.prototype.hasOwnProperty.call(rank, key)) {
          const element = rank[key];
          baseItem[key] = element;
        }
      }
    });
    // Add the children back for the drop down.
    // baseItem.children = item.children;
    finalRank.push(baseItem);
  });
  finalRank.forEach((item) => {
    const childrenCopy = dataFlatterned.find(
      (flatItem) => flatItem.InstrumentType == item.InstrumentType,
    )?.children;

    if (childrenCopy) {
      item.children = childrenCopy;
    }
  });

  return finalRank;
}

const blue = "#3A82F6";
const lightBlue = "#001e4f";

function buildColDefs(allCPs: string[]): ColDef[] {
  const colDefState: ColDef[] = [
    {
      // checkboxSelection: true,
      enableCellChangeFlash: false,
      maxWidth: 30,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      lockPosition: "left", // This ensures the column stays in the first position
      suppressColumnsToolPanel: true,
      cellRenderer: "agGroupCellRenderer",
      // headerCheckboxSelectionFilteredOnly: true,
    },
    { field: "InstrumentType", colId: "InstrumentType", maxWidth: 150 },
  ];
  allCPs.forEach((cp: string) => {
    colDefState.push({
      field: cp,
      colId: cp,
      maxWidth: 110,
      cellStyle: (params): { [key: string]: string | number } => {
        const rank = params.value; // rank in the cell
        if (!rank) return {};
        const color = interpolateColorRankSpecific(
          blue,
          lightBlue,
          params.value,
          params.data,
        );

        return {
          display: "flex",
          alignItems: "center",
          backgroundColor: color,
          color: params.value == 1 ? "white" : "lightgray", // keep text readable
        };
      },
      cellRenderer: (params: any) => {
        if (!params.value) {
          return "";
        }
        if (params.value === 1) {
          return (
            <div className="flex h-full w-full flex-row">
              {params.value} <div className="h-5 w-5 pl-1">{star}</div>
            </div>
          );
        }
        return params.value;
      },
    });
  });
  return colDefState;
}

function buildDetailColDefs(
  theme: string | undefined,
  allCPs: string[],
): ColDef[] {
  const colDefState: ColDef[] = [
    {
      field: "field",
      colId: "filed",
      pinned: "left",
      suppressMovable: true,
      maxWidth: 155,
      cellRenderer: "agGroupCellRenderer",
      headerName: "",
      headerClass:
        theme == "light"
          ? "analytics-headerCells"
          : "analytics-headerCellsDark",
      cellStyle: () => ({
        backgroundColor: theme == "light" ? "#FFFFFF" : "#1B1919",
      }),
    },
  ];

  allCPs.forEach((cp: string) =>
    colDefState.push({
      field: cp,
      headerName: cp,
      colId: cp,
      maxWidth: 110,
      headerClass:
        theme == "light"
          ? "analytics-headerCells"
          : "analytics-headerCellsDark",
      cellStyle: (params): { [key: string]: string | number } => {
        const arrsToRank = ["HitRate", "DV01Total"]; // these will have the higher value as desired.
        // Fields OUTSIDE this will have the LOWER value as desired.

        const arrToSearch = arrsToRank.includes(params.data.field)
          ? [...params.data.sortedArr].reverse()
          : [...params.data.sortedArr];

        let nonStandardRank =
          arrToSearch.findIndex((obj: { name: string; value: number }) => {
            if (params.data.field == "HitRate") {
              // Must round for hitRate as it's a %.
              return Math.round(obj.value) == params.value;
            }
            return obj.value == params.value;
          }) + 1;

        if (params.data.field == "Spread" && params.value == 0) {
          nonStandardRank = params.data.sortedArr.length;
        }
        if (params.data.field == "TimeToQuote" && params.value == 0) {
          nonStandardRank = params.data.sortedArr.length;
        }

        const rank = nonStandardRank >= 0 ? nonStandardRank + 1 : null;

        const totalRanks = Object.keys(params.data.sortedArr).length; // max rank

        // DROP DOWN TABLE COLOUR
        //Drop down table adjusted so lighter colour is better to match main table.

        const blue = "#e9ebf0";
        const lightBlue = "#8D99AF";

        if (!rank || !totalRanks)
          return {
            backgroundColor: theme == "light" ? "#FFFFFF" : "#1B1919",
          };

        // normalize rank → 0–1
        const factor = (rank - 1) / totalRanks;
        if (factor == 0) {
          return {
            backgroundColor: theme == "light" ? "#FFFFFF" : "#1B1919",
            // color: factor < 0.3 ? "white" : "black", // keep text readable
          };
        }

        // interpolate color
        const color = interpolateColor(blue, lightBlue, factor);

        // Early return for hit rates of 0
        const fieldsToReturnAtZero = ["HitRate"];
        if (
          fieldsToReturnAtZero.includes(params.data.field) &&
          params.value == 0
        ) {
          // Always show 0% as a last place.
          return {
            backgroundColor: lightBlue,
            color: "black",
          };
        }
        return {
          backgroundColor: totalRanks == 1 ? "#77A9F8" : color, // #77A9F8 is blue with the average first factor applied.
          color: rank - 1 == 1 ? "black" : "black", // keep text readable
        };
      },
      valueFormatter: (params) => {
        if (params.data.field == "DV01Total" && params.value) {
          return `$${Number(params.value).toLocaleString()}`;
        } else if (params.data.field == "Spread" && params.value) {
          return `${Number(params.value)}`;
        }
        const fieldsToFormatPercent = ["HitRate"];
        if (
          fieldsToFormatPercent.includes(params.data.field) &&
          params.value >= 0
        ) {
          return `${params.value} %`;
        }
        return params.value;
      },
    }),
  );

  return colDefState;
}

interface IFlattenedChildren {
  field: string;
}

export interface IFlattenedData {
  id: string;
  InstrumentType: string;
  children: IFlattenedChildren[];
  ranks: any[];
  [key: string]: unknown;
}

const BrokerRankMatrix = ({
  dataFlatterned,
  dataFilters,
  allCPs,
}: {
  dataFlatterned: IFlattenedData[];
  dataFilters: IBaseFilterBrokerRank;
  allCPs: string[];
}) => {
  const { theme } = useTheme();
  const gridRef = useRef<AgGridReact>(null);

  const [data, setData] = useState(buildBrokerRankData(dataFlatterned, allCPs));

  useEffect(() => {
    setData(buildBrokerRankData(dataFlatterned, allCPs));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFlatterned, dataFilters]);

  const style = useMemo(
    () => GenerateBrokerRankStyles(theme) as React.CSSProperties,
    [theme],
  );

  const [colDefs, setColDefs] = useState(buildColDefs(allCPs));

  useEffect(() => {
    setColDefs(buildColDefs(allCPs));
  }, [allCPs]);

  const getRowId = useCallback((params: GetRowIdParams) => {
    return params.data.InstrumentType;
  }, []);

  const columnOrderRef = useRef<ColumnState[] | null>(null);
  const openDetailGridsRef = useRef<GridApi[]>([]);

  const detailCellRendererParams = useMemo(() => {
    return () => {
      const res = {} as IDetailCellRendererParams | any;

      res.getDetailRowData = (params: GetDetailRowDataParams<any>) => {
        params.successCallback(params.data.children);
      };

      res.detailGridOptions = {
        detailRowAutoHeight: true,
        columnDefs: buildDetailColDefs(theme, allCPs),
        defaultColDef: {
          suppressMovable: true, // Prevent the drop down table from getting moved by their level. All movement of column ordering should happen at the master table level.
        },
        className: "cssStylingOnNestedTable1",
        getRowId: (childParams: ICellRendererParams) => {
          return childParams.data?.field?.toString();
        },
        onGridReady: (params: GridReadyEvent) => {
          openDetailGridsRef.current.push(params.api);

          params.api.addEventListener("gridDestroyed", () => {
            openDetailGridsRef.current = openDetailGridsRef.current.filter(
              (api) => api !== params.api,
            );
          });
          // Apply initial column order
          setTimeout(() => {
            if (columnOrderRef.current) {
              params.api.applyColumnState({
                state: columnOrderRef.current,
                applyOrder: true,
              });
            }
          }, 0);
        },
      };
      return res;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  return (
    <div className="flex h-full w-full flex-col">
      <div
        className={`${theme == "dark" ? "ag-theme-balham-dark" : "ag-theme-balham"} flex h-full w-full flex-col  `}
        style={style}
      >
        <div className="w-full pb-1 pl-2 text-lg">Broker Rank Matrix</div>
        <AgGridReact
          ref={gridRef}
          rowData={data}
          columnDefs={colDefs}
          columnMenu={"new"} //do not delete, affects header menus
          detailRowAutoHeight={true}
          getRowId={getRowId}
          // rowSelection={"single"}
          suppressAggFuncInHeader={true}
          suppressDragLeaveHidesColumns={true}
          // Style:
          rowHeight={globalAGGridTableHeight}
          scrollbarWidth={6}
          groupDisplayType={"groupRows"}
          animateRows={true} // Smoother transition of rows when updates occur - needs 'getRowId' for this to work.
          maintainColumnOrder={true}
          // components={}
          tooltipShowDelay={1}
          domLayout="normal"
          masterDetail={true}
          detailCellRendererParams={detailCellRendererParams}
          onColumnMoved={(params) => {
            const newState = params.api.getColumnState();
            columnOrderRef.current = newState;
            openDetailGridsRef.current.forEach((api) => {
              if (!api.isDestroyed()) {
                api.applyColumnState({
                  state:
                    columnOrderRef.current?.filter(
                      (c) => c.colId !== "fixed",
                    ) ?? [],
                  applyOrder: true,
                });
              }
            });
          }}
        />
      </div>
    </div>
  );
};

export default BrokerRankMatrix;
